import * as React from 'react'
import * as PropTypes from 'prop-types'
import { Redirect, useHistory } from 'react-router'

import validateOtp from '../../../../services/validation/OTP'
import { postDevices } from '../../../../services/api/requests/Device'
import {
  LANDING,
  OTP,
  ADD_CARD,
} from '../../../../services/data/registrationSteps.json'
import AccessibleForm from '../../../../components/accessibility/AccessibleForm/AccessibleForm'
import {
  setGACustomPageView,
  setGACustomEvent,
} from '../../../../services/analytics/google-analytics'

// simplified imports
import ErrorModal from './ErrorModal'
import OtpForm from '../../../../components/ui/SimplifiedLogin/OtpForm/OtpForm'
import { Layout as MainLayout } from '../../../../components/ui/SimplifiedLogin'
import {
  Title,
  Description,
  FormWrapper,
  ContentContainer,
} from '../../../simplified.login/components/MainView/MainView.style'
import useFeatureMaintenance, {
  FEATURES_LIST,
} from '../../../../services/hooks/useFeatureMaintenance'
import { REGISTRATION_STEPS } from '../../../../services/hooks/useRegistrationBanners'

function MainView({
  step,
  primaryInfo,
  token,
  firebaseMessagingToken,
  setStep,
  push,
  replace,
  setAccessToken,
  doVerifyOtp,
  doResendOtp,
  browser,
  setDevice,
  location = {},
  setFirstSpinModalOpen,
  setAccountStatus,
}) {
  const { mobileNumber } = primaryInfo || {}
  const [smsMode, setSMSMode] = React.useState(true)
  const rrHistory = useHistory()
  const [showErrorModal, setShowErrorModal] = React.useState(false)
  const {
    loading,
    isFeatureAvailable,
    refreshFeatureStatus,
  } = useFeatureMaintenance({ feature: FEATURES_LIST.EMAIL_OTP })

  const resend = async ({ toMobile, setLoading, setError }) => {
    setLoading(true)
    setError(null)
    setSMSMode(toMobile)
    setGACustomEvent('button_cta', {
      screen_name: 'Registration - OTP Verification',
      button_name: `Resend - Receive via ${toMobile ? 'SMS' : 'Email'}`,
    })

    try {
      await doResendOtp({ token, toMobile })
    } catch (error) {
      const { data = {} } = error.response || {}
      const { message = 'Network Error' } = data || {}
      setError(message)
    }
    setLoading(false)
  }

  const handleModalConfirm = () => {
    replace(`/login${location.search || ''}`)
    setShowErrorModal(false)
  }

  React.useEffect(() => {
    refreshFeatureStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    return () => {
      if (rrHistory.action === 'POP' && showErrorModal) {
        handleModalConfirm()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrorModal])

  React.useEffect(() => {
    if (location)
      setGACustomPageView({
        page: location.pathname,
        title: 'Registration - OTP Verification',
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  React.useEffect(() => {
    if (window.document.cookie.includes('web_uuid=')) {
      return
    }
    const { name, os, version } = browser
    postDevices({
      platform: 'web',
      deviceName: name,
      deviceModel: os,
      osVersion: version,
    }).then(res => {
      setDevice(res.data)
    })
  }, [browser, setDevice])

  if (step !== OTP && step !== ADD_CARD) {
    return <Redirect to={`/register${location.search || ''}`} />
  }

  return (
    <MainLayout registrationStep={REGISTRATION_STEPS.REGISTER_OTP}>
      <ContentContainer minHeight={420}>
        <div>
          <Title>We want to know it’s really you.</Title>
          <Description>
            Enter the code we sent&nbsp;
            {!smsMode ? (
              <>to your registered email address</>
            ) : (
              <>
                via SMS to your registered mobile number&nbsp;
                <span>
                  {mobileNumber && `ending in ${mobileNumber.slice(-4)}`}
                </span>
              </>
            )}
          </Description>
          <FormWrapper>
            <AccessibleForm
              enableReinitialize
              initialValues={{}}
              validate={validateOtp}
              onSubmit={doVerifyOtp({
                push,
                token,
                setStep,
                setAccessToken,
                setAccountStatus,
                setShowErrorModal,
                setFirstSpinModalOpen,
                firebaseMessagingToken,
                searchString: location.search,
                crmContactId: location.state?.crmContactId,
              })}
              method="post"
              action="/a/otp/submit"
              componentProps={{
                resend,
                removeEmailOTP: !(isFeatureAvailable() || loading),
              }}
              component={OtpForm}
            />
          </FormWrapper>
        </div>
        <div />
      </ContentContainer>
      <ErrorModal show={showErrorModal} onConfirmClick={handleModalConfirm} />
    </MainLayout>
  )
}

MainView.defaultProps = {
  primaryInfo: null,
  token: null,
  step: LANDING,
}

MainView.propTypes = {
  step: PropTypes.number,
  primaryInfo: PropTypes.shape(),
  token: PropTypes.string,
  firebaseMessagingToken: PropTypes.string.isRequired,
  setStep: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  setAccessToken: PropTypes.func.isRequired,
  doVerifyOtp: PropTypes.func.isRequired,
  doResendOtp: PropTypes.func.isRequired,
}

export default MainView
